import React, { useEffect, useState } from "react";
import {
  Table,
  Thead,
  Tr,
  Td,
  Tbody,
  TableContainer,
  Th,
  Text,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  useDisclosure,
  Button,
  Icon,
  Box,
} from "@chakra-ui/react";
import { IoMdAdd } from "react-icons/io";
import {
  ColumnValuesList,
  NodeInsights,
} from "../../containers/Interfaces";
import { useAnalysisTreeContext } from "../../context/AnalysisTreeContext";
import { CustomBenchmark } from "../../utils/enum";
import { v4 as uuidv4 } from "uuid";
import { 
  AnalysisColumnType,
  ColumnType, 
  MetricEdgeType 
} from "build-ts/enums";
import { 
  BenchmarkProto, 
  FilterProto, 
  FormatProto, 
  Header, 
  MetricEdgeProto, 
  MetricProto, 
  ObservationColumnProto 
} from "build-ts/metadata";
import { 
  GrowthBenchmarkProto,
  GrowthBenchmarkType 
} from "build-ts/relative_date";
import { AnalysisType } from "build-ts/analysis";
import { formatHeaderName, getAnalysisColumnTypeDisplay } from "utils/nameUtils";

interface DataTableProps {
  node: NodeInsights;
  showColumn: boolean;
  showHiddenColumns: boolean;
  onToggleHiddenColumns: () => void;
  isChatOpen: boolean;
}

const DataTable: React.FC<DataTableProps> = ({ 
  node, 
  showColumn, 
  showHiddenColumns, 
  onToggleHiddenColumns,
  isChatOpen 
}) => {

  // Destructure necessary values from context.
  const { 
    transactionId, 
    mkgByNodeId, 
    rootNodeId, 
    nodeInfoMap, 
    tree, 
    onEdgeClick,
    treePannelLoader
  } = useAnalysisTreeContext();
  // Use useDisclosure to control the Menu's visibility
  const { onOpen, onClose } = useDisclosure();
  
  // State management for mapping column values, and tracking hover states.
  const [columnValuesMap, setColumnValuesMap] = useState<Map<number, ColumnValuesList>>(new Map());
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [filteredEdges, setFilteredEdges] = useState<MetricEdgeProto[]>([]);

  // Extracting observation columns and column values list from the provided data.
  const observation_columns = node && node?.current_node.analysed_data?.observation?.content?.observation_columns;
  const column_values_list = node && node?.current_node.analysed_data?.column_values_list;

  // useEffect to populate the columnValuesMap based on the provided data.
  // This includes parsing JSON values and adjusting column types for display.
  useEffect(() => {
    const newColumnValuesMap = new Map<number, ColumnValuesList>();

    if (column_values_list) {
      column_values_list.forEach((columnValues: ColumnValuesList, index) => {
        if (columnValues.data_value_json_array) {

          // Replace "NaN" with a placeholder string
          const jsonString = columnValues.data_value_json_array.replace(/NaN/g, "'_NaN_'");
        
          // Parse the JSON string
          let parsedData = JSON.parse(jsonString);

          // Replace the placeholder string with NaN
          parsedData = parsedData.map((value: any) => value === "_NaN_" ? NaN : value);

          // Store parsed JSON array in the columnValues object
          columnValues.parsed_data_value_json_array = parsedData ?? [];
        }

        if (columnValues.analysis_column_type) {
          columnValues.analysis_column_type_display = getAnalysisColumnTypeDisplay(columnValues?.analysis_column_type);
        }

        // Fallback logic for column type based on observation columns or benchmark headers
        const observationColumn = observation_columns?.find(col => col.column_id === columnValues.column_id);
        if (observationColumn) {
          columnValues.analysis_column_type_display = observationColumn?.logical_column_header?.name?.toUpperCase();
        } else {
          // Check in current benchmark header
          const benchmarkHeader = node?.current_node?.benchmark?.header;
          if (columnValues.column_id === benchmarkHeader?.id) {
            columnValues.analysis_column_type_display = benchmarkHeader?.name?.toUpperCase();
          } 
          // Check in benchmark_headers array regardless of whether a single benchmark header matched
          else if (node.current_node && node.current_node.analysed_data?.benchmark_headers) {
            const benchmarkHeaders = node.current_node.analysed_data?.benchmark_headers;
            benchmarkHeaders?.forEach((header: Header) => {
              if (columnValues.column_id === header?.id) {
                columnValues.analysis_column_type_display = header?.name?.toUpperCase();
              }
            });
          }
        }

        // Store the processed column values in the map
        newColumnValuesMap.set(index, columnValues);
      });
    }

    setColumnValuesMap(newColumnValuesMap);
  }, [node, column_values_list, observation_columns]);

  const isVarianceColumn = (columnIndex: number): boolean => {
    const columnValues = columnValuesMap.get(columnIndex);
    return columnValues?.analysis_column_type? columnValues?.analysis_column_type === AnalysisColumnType.VARIANCE: false;
  };

  const isMaterialColumn = (columnIndex: number): boolean => {
    const columnValues = columnValuesMap.get(columnIndex);
    const columnId = columnValues?.column_id;
    const observationColumn = observation_columns?.find(col => col.column_id === columnId);
    return observationColumn?.is_material ? observationColumn.is_material === true: false;
  };

  const isVariancePercentageColumn = (columnIndex: number): boolean => {
    const columnValues = columnValuesMap.get(columnIndex);
    return columnValues?.analysis_column_type? columnValues?.analysis_column_type === AnalysisColumnType.VARIANCE_PERCENTAGE: false;
  };

  const isVarianceContributionColumn = (columnIndex: number): boolean => {
    const columnValues = columnValuesMap.get(columnIndex);
    return columnValues?.analysis_column_type? columnValues?.analysis_column_type === AnalysisColumnType.VARIANCE_CONTRIBUTION: false;
  };

  const formatValue = (
    value: number,
    formatSettings: FormatProto | null,
    isMaterial: boolean,
    isVariancePercentage: boolean,
    isVarianceContribution: boolean,
    isVariance: boolean,
  ): string => {

    // If it's a material value, return as it is.
    if (isMaterial) {
      return value.toString();
    }

    if(isVariance){
      let formattedValue = "";
      if (formatSettings && formatSettings.percentage) {
        formattedValue = (value * 100).toFixed(2) + "%";
      } else {
        formattedValue = Number(value.toFixed(2)).toLocaleString("en-US");
      }

      return value < 0 ? `(${formattedValue})` : formattedValue;
    }

    if(isVarianceContribution){
      const formattedValue = value.toFixed(2);
      return value < 0 ? `(${formattedValue})` : formattedValue;
    }

    if (isVariancePercentage) {
      const formattedValue = (value * 100).toFixed(2) + "%";
      return value < 0 ? `(${formattedValue})` : formattedValue;
    }

    if (formatSettings && formatSettings.percentage) {
      const formattedValue = (value * 100).toFixed(2) + "%";
      return value < 0 ? `${formattedValue}` : formattedValue;
    }

    // Otherwise, format as a localized string
    const formattedValue = Number(value.toFixed(2)).toLocaleString("en-US");
    return value < 0 ? `${formattedValue}` : formattedValue;
  };

  const transposedData: any[][] = [];
  columnValuesMap.forEach((columnValues) => {
    columnValues.parsed_data_value_json_array?.forEach((item, index) => {
      if (!transposedData[index]) {
        transposedData[index] = [];
      }
      transposedData[index].push(item);
    });
  });

  // Format each cell in the transposed data for display in the table
  const formattedData = transposedData?.map((row) => {
    const identifier = row[0];
    let formatSettings: FormatProto | null = null;

    // Check if inherited_mkg and correlated_mkgs exist before proceeding
    const inherited_mkg: MetricProto | undefined = mkgByNodeId?.get(node?.current_node.id!)?.inherited_mkg;
    const allMKGS: MetricProto[] = mkgByNodeId?.get(node?.current_node?.id!)?.correlated_mkgs ? [...mkgByNodeId.get(node?.current_node?.id!)?.correlated_mkgs!] : [];
    if(inherited_mkg) allMKGS.push(inherited_mkg);
    
    // Find the format setting by matching header.name with the identifier
    for (const mkg of allMKGS) {
      if (mkg?.header?.name === identifier) {
          formatSettings = mkg?.format ? mkg.format : null;
          break; // Exit loop once a match is found
      }
    }
    if (!isNaN(row[0]) || formatSettings === null) formatSettings = inherited_mkg?.format ? inherited_mkg?.format : null;
    // Apply formatting logic to each value in the row
    return row.map((value, colIndex) => {
      const numValue = typeof value !== "string"? parseFloat(value as string): value;
      //if numValue is string e.g 2nd year upsell then it should not parse as number
      if (typeof numValue !== "string" && !isNaN(numValue)) {
        return formatValue(
          numValue,
          formatSettings,
          isMaterialColumn(colIndex),
          isVariancePercentageColumn(colIndex),
          isVarianceContributionColumn(colIndex),
          isVarianceColumn(colIndex)
        );
      }
      return value;
    });
  });

  /**
   * Function to filter edges based on child nodes' analysis type.
   * It removes edges that are already linked to specific child node analyses
   * like DRIVER_ANALYSIS, CORRELATION_ANALYSIS, BENCHMARK_ANALYSIS, etc.
   */
  const filterEdgesBasedOnChildNodes = (
    data: NodeInsights,
    rowIndex: number,
    formattedData: any[][],
    edges: MetricEdgeProto[],
  ): MetricEdgeProto[] => {
    const childrenIds = data.current_node.children_ids || [];

    for (const childId of childrenIds) {
      const childNode = nodeInfoMap?.get(childId);
      if (!childNode) {
        return edges;
      }

      // If the current node is performing DRIVER_ANALYSIS
      if (data.current_node.analysis_type === AnalysisType.DRIVER_ANALYSIS) {

        // Get the filter headers used by the current node
        const currentNodeFilterHeaders = new Set(
          data?.current_node.observation?.content?.filters.map(
            (filter: FilterProto) => filter?.logical_column_header?.id
          )
        );
        
        // Find filters that are unique to the child node, not in the current node
        const uniqueFilters: FilterProto[] | undefined=
          childNode?.current_node?.observation?.content?.filters.filter(
            (filter) => !currentNodeFilterHeaders.has(filter?.logical_column_header?.id)
          );
        
        // Extract the edge name based on unique filters
        const edgeName = uniqueFilters?.map((filter: FilterProto) => {
            return `${filter.values && filter.values.join(", ")}`;
          }).join(", ");
        
        // If the child node is also performing DRIVER_ANALYSIS, remove used ATTRIBUTE_EDGE from edges
        if (childNode.current_node.analysis_type === AnalysisType.DRIVER_ANALYSIS) {
          if (formattedData[rowIndex][0] === edgeName) {
            edges = edges.filter(
              (edge) =>
                !(edge.edge_type === MetricEdgeType.ATTRIBUTE_EDGE &&
                  edge.related_attribute?.name === childNode.current_node.driver_header?.name!)
            );
          }
        }

        // If the child node is performing CORRELATION_ANALYSIS, remove used FUNDAMENTAL_EDGE from edges 
        if (childNode.current_node.analysis_type === AnalysisType.CORRELATION_ANALYSIS) {
          if (formattedData[rowIndex][0] === edgeName) {
            edges = edges.filter(
              (edge) =>
                !(edge.edge_type === MetricEdgeType.FUNDAMENTAL_EDGE &&
                  edge.fundamental_relationship?.header?.name === childNode.current_node.fundamental_correlation_header?.name!)
            );
          }
        }
        // If the current node is performing CORRELATION_ANALYSIS
      } else if (data.current_node.analysis_type === AnalysisType.CORRELATION_ANALYSIS) {

        // Get the observation columns from the child node
        const observationColumns = childNode?.current_node.observation?.content?.observation_columns;

        // Get the edge name
        const edgeName = observationColumns
          ?.map((column: ObservationColumnProto) => column?.logical_column_header?.name)
          .join(", ");

        // Remove used FUNDAMENTAL_EDGE from edges for CORRELATION_ANALYSIS child nodes
        if (childNode.current_node.analysis_type === AnalysisType.CORRELATION_ANALYSIS) {
          if (formattedData[rowIndex][0] === edgeName) {
            edges = edges.filter(
              (edge) =>
                !(edge.edge_type === MetricEdgeType.FUNDAMENTAL_EDGE &&
                  edge.fundamental_relationship?.header?.name === childNode.current_node.fundamental_correlation_header?.name!)
            );
          }
        }
        
        // Remove used ATTRIBUTE_EDGE from edges for DRIVER_ANALYSIS child nodes
        if (childNode.current_node.analysis_type === AnalysisType.DRIVER_ANALYSIS) {
          if (formattedData[rowIndex][0] === edgeName) {
            edges = edges.filter(
              (edge) =>
                !(edge.edge_type === MetricEdgeType.ATTRIBUTE_EDGE &&
                  edge.related_attribute?.name === childNode.current_node.driver_header?.name)
            );
          }
        }
        // If the current node is performing BENCHMARK_ANALYSIS
      } else if (data.current_node.analysis_type === AnalysisType.BENCHMARK_ANALYSIS) {

        // Remove used FUNDAMENTAL_EDGE from edges for CORRELATION_ANALYSIS child nodes
        if (childNode.current_node.analysis_type === AnalysisType.CORRELATION_ANALYSIS) {
          edges = edges.filter(
            (edge) =>
              !(edge.edge_type === MetricEdgeType.FUNDAMENTAL_EDGE &&
                edge.fundamental_relationship?.header?.name === childNode.current_node.fundamental_correlation_header?.name!)
          );
        }

        // Remove used ATTRIBUTE_EDGE from edges for DRIVER_ANALYSIS child nodes
        if (childNode.current_node.analysis_type === AnalysisType.DRIVER_ANALYSIS) {
          edges = edges.filter(
            (edge) =>
              !(edge.edge_type === MetricEdgeType.ATTRIBUTE_EDGE &&
                edge.related_attribute?.name === childNode.current_node.driver_header?.name)
          );
        }

        // If the current node is performing METRIC_VALUE_ANALYSIS
      } else if (data.current_node.analysis_type === AnalysisType.METRIC_VALUE_ANALYSIS) {

        // Remove BENCHMARK_EDGE from edges for BENCHMARK_ANALYSIS child nodes
        if (childNode.current_node.analysis_type === AnalysisType.BENCHMARK_ANALYSIS) {
          edges = edges.filter(
            (edge) =>
              !(edge.edge_type === MetricEdgeType.BENCHMARK_EDGE &&
                edge.benchmark?.header?.name === childNode?.current_node?.benchmark?.header?.name!)
          );
        }
      }
    }
    return edges;
  };

  const getFilteredEdges = (rowIndex: number): MetricEdgeProto[] => {
    let edges: MetricEdgeProto[] = [];
  
    // Retrieve inherited and correlated MKGs
    const inherited_mkg: MetricProto = mkgByNodeId?.get(node?.current_node.id!)?.inherited_mkg!;
    const correlated_mkgs = mkgByNodeId?.get(node?.current_node.id!)?.correlated_mkgs
      ? [...mkgByNodeId.get(node?.current_node?.id!)?.correlated_mkgs!]
      : [];
  
    // Find the correlated metric that matches the formatted data for the given row
    if (correlated_mkgs.length > 0) {
      let correlatedMetric: MetricProto | null = null;
      for (const mkg of correlated_mkgs) {
        if (mkg?.header?.name === formattedData[rowIndex][0]) {
          correlatedMetric = mkg;
          break;
        }
      }
      
      // Assign edges from the correlated metric or the inherited MKG
      edges = correlatedMetric ? [...correlatedMetric.edges!] : [...(inherited_mkg?.edges || [])];
    } else {
      // If no correlated MKGs, fallback to inherited MKG edges
      edges = [...(inherited_mkg?.edges || [])];
    }
  
    // Handle the case where the current node is the root node and use filters from the context
    if (node?.current_node.id === rootNodeId && tree?.context) {
      edges = [...edges];
  
      let temporalFilters: FilterProto[] = [];
  
      // Iterate over the tree context to find temporal filters (GTE and LTE)
      for (const filter of tree.context) {
        const columnType = filter.logical_column?.column_type;
  
        if (columnType === ColumnType.TEMPORAL) {
          temporalFilters.push(filter);
        }
      }
  
      // Create benchmarks if GTE and LTE filters exist
      if (temporalFilters.length > 0) {
        const priorPeriodType: GrowthBenchmarkProto = {
          growth_type: GrowthBenchmarkType.PRIOR_PERIOD
        };
        const priorYearType: GrowthBenchmarkProto = {
          growth_type: GrowthBenchmarkType.PRIOR_YEAR
        };
        // Create Previous Period benchmark with specific ID and name
        const priorPeriodBenchmark: BenchmarkProto = {
          header: {id: uuidv4(), name: CustomBenchmark.PreviousPeriod},
          benchmark_filters:[],
          variance_tolerance: 0.0,
          growth_benchmarks:[priorPeriodType]
        };
        
        // Create Same Period Last Year benchmark with specific ID and name
        const priorYearBenchmark: BenchmarkProto = {
          header: {id: uuidv4(), name: CustomBenchmark.SamePeriodLastYear},
          benchmark_filters:[],
          variance_tolerance: 0.0,
          growth_benchmarks: [priorYearType]
        };

        const benchmarkEdges: MetricEdgeProto[] = [
          { edge_type: MetricEdgeType.BENCHMARK_EDGE, benchmark: priorPeriodBenchmark },
          { edge_type: MetricEdgeType.BENCHMARK_EDGE, benchmark: priorYearBenchmark },
        ];
  
        const rootNodeBenchmarkEdges: MetricEdgeProto[] =
          mkgByNodeId.get(node.current_node.id)?.inherited_mkg?.edges?.filter(
            (edge: MetricEdgeProto) => edge.edge_type === MetricEdgeType.BENCHMARK_EDGE
          ) || [];
  
        benchmarkEdges.push(...rootNodeBenchmarkEdges);
        edges = benchmarkEdges;
      }
    }
  
    // Further filter edges based on child nodes
    edges = filterEdgesBasedOnChildNodes(node, rowIndex, formattedData, edges);
  
    return edges;
  };

  const handleMenuOpen = (rowIndex: number) => {
    const edges = getFilteredEdges(rowIndex); // Fetch the filtered edges for the specific row
    setFilteredEdges(edges);
    setSelectedRowIndex(rowIndex); // Highlight the row
    onOpen();
  };

  const handleMenuClose = () => {
    setSelectedRowIndex(null); // Remove the highlight when the menu is closed
    onClose();
  };

  return (
    <TableContainer
      p={0}
      key={node && node.current_node.id}
      maxHeight="330px"
      overflowY="auto"
      css={{
        scrollbarWidth: "thin",
        scrollbarColor: "#CBD5E0 transparent",
        "&::-webkit-scrollbar": {
          height: "3px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#CBD5E0",
          borderRadius: "3px",
        },
      }}
      m={1}
    >
      <Table size={isChatOpen ? "sm" : "md"} variant="simple" w="100%" align="center">
        <Thead>
          <Tr>
            {columnValuesMap &&
              Array.from(columnValuesMap.entries()).map(
                ([index, columnValues]) => {
                  if (
                    !showHiddenColumns &&
                    (isMaterialColumn(index) ||
                      (columnValues.analysis_column_type &&
                        (columnValues.analysis_column_type ===
                          AnalysisColumnType.VARIANCE_CONTRIBUTION ||
                          columnValues.analysis_column_type ===
                          AnalysisColumnType.VARIANCE_PERCENTAGE)))
                  ) {
                    return null;
                  }

                  return (
                    <Th
                      key={columnValues.column_id}
                      color="gray.600"
                      fontSize={isChatOpen ? "2xs" : "xs"}
                      bg="gray.50"
                      fontWeight="700"
                      textAlign={index !== 0 ? "right" : "left"}
                      whiteSpace="normal"
                      overflow="visible"
                      textOverflow="ellipsis"
                      position="sticky"
                      top="0"
                      px={2}
                      py={isChatOpen ? 1 : 1}
                      left={index === 0 ? "0" : "auto"}
                      zIndex={index === 0 ? 2 : 1}
                      width={`${100 / columnValuesMap.size}%`}
                    >
                      <Text mb={0} size="md">
                          {formatHeaderName(columnValues?.analysis_column_type_display!)}
                      </Text>
                    </Th>
                  );
                }
              )}
          </Tr>
        </Thead>
        <Tbody fontWeight={600}>
          {formattedData?.map((row, rowIndex) => (
            <Tr
              key={rowIndex}
              bg="bg.surface"
              _hover={{ bg: "gray.100" }}
            >
              {row.map((column, colIndex) => {
                if (
                  !showHiddenColumns &&
                  (isMaterialColumn(colIndex) ||
                    (columnValuesMap.get(colIndex)?.analysis_column_type &&
                      (columnValuesMap.get(colIndex)?.analysis_column_type ===
                      AnalysisColumnType.VARIANCE_CONTRIBUTION ||
                        columnValuesMap.get(colIndex)?.analysis_column_type ===
                        AnalysisColumnType.VARIANCE_PERCENTAGE)))
                ) {
                  return null;
                }

                // Define styles and conditions for the first column click
                const isFirstColumn = colIndex === 0;
                const isSelectedRow = selectedRowIndex === rowIndex;
                const isNegative =
                  typeof column === "string" && column.startsWith("(");
                const isNaNValue = Number.isNaN(column);
                const color =
                  !isNaNValue &&
                  (isVarianceColumn(colIndex) ||
                    isVarianceContributionColumn(colIndex) ||
                    isVariancePercentageColumn(colIndex)) &&
                  column !== null
                    ? isNegative
                      ? "red"
                      : "green"
                    : "inherit";

                return (
                  <Td
                    key={colIndex}
                    color={color}
                    textAlign={
                      isFirstColumn && typeof column !== "number"
                        ? "left"
                        : "right"
                    }
                    p={0}
                    pr={isFirstColumn ? 0: 2}
                    m={0}
                    maxW={isFirstColumn ? "100px" : "auto"}
                    whiteSpace="normal"
                    overflow={isFirstColumn ? "visible" : "hidden"}
                    textOverflow="ellipsis"
                    position={isFirstColumn ? "sticky" : "static"}
                    left={isFirstColumn ? "0" : "auto"}
                    zIndex={isFirstColumn ? 1 : "auto"}
                    cursor={isFirstColumn ? "pointer" : "auto"}
                    bg="inherit"
                    width={`${100 / columnValuesMap.size}%`}
                  >
                    {/* Only make the first column clickable */}
                    {isFirstColumn ? (
                      <Menu placement="bottom-start" flip={true} onClose={handleMenuClose}>
                        <MenuButton
                          as={Button}
                          rightIcon={
                            <Box _groupHover={{ "& > svg": { boxSize: 5, color: "white" } }}>
                              <Icon 
                                as={IoMdAdd} 
                                color="white"
                                boxSize={5}
                                transition="all 0.2s"
                              />
                            </Box>
                          }
                          role="group"
                          onClick={() => handleMenuOpen(rowIndex)}
                          px={1}
                          m={0}
                          height="auto"
                          minHeight="20px"
                          color={isFirstColumn && isSelectedRow ? "white": "black"}
                          cursor="pointer"
                          width="100%"
                          textAlign="left"
                          display="flex"
                          justifyContent="space-between"
                          _hover={{ bg: !treePannelLoader ? "purple.700" : "inherit", color: !treePannelLoader ? "white" : "black" }}
                          bg={isSelectedRow && isFirstColumn ? "purple.700" : "inherit"}
                          isDisabled={treePannelLoader}
                          wordBreak="break-word"
                          whiteSpace="normal"
                        >
                          <Text 
                            as="span" 
                            fontSize={isChatOpen ? "xs" : "sm"} 
                            m={0} 
                            ml={1} 
                            p={0}
                            textAlign="left"
                            display="block"
                            width="100%"
                          >
                            {column !== null ? column : "null"}
                          </Text>
                        </MenuButton>
                        <Portal>
                          <MenuList
                            zIndex={25}
                            maxH="150px"
                            overflow="auto"
                            m={0}
                            p={0}
                          >
                            {filteredEdges?.length > 0 ? (
                              filteredEdges?.map((edge: MetricEdgeProto, i: number) => (
                                <MenuItem
                                  key={i}
                                  onClick={() =>
                                    onEdgeClick(
                                      node,
                                      edge,
                                      rowIndex,
                                      transactionId
                                    )
                                  }
                                  fontSize={isChatOpen ? "xs" : "sm"}
                                  fontWeight={600}
                                >
                                  {edge.edge_type ===
                                  MetricEdgeType.BENCHMARK_EDGE
                                    ? `Compare to ${edge?.benchmark?.header?.name}`
                                    : edge.edge_type ===
                                      MetricEdgeType.ATTRIBUTE_EDGE
                                    ? `Split by ${edge?.related_attribute?.name}`
                                    : edge.edge_type ===
                                      MetricEdgeType.FUNDAMENTAL_EDGE
                                    ? `Split by ${edge?.fundamental_relationship?.header?.name}`
                                    : "Unknown Edge Type"}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem
                                fontSize={isChatOpen ? "xs" : "sm"}
                                fontWeight={600}
                                isDisabled
                              >
                                No suggestions
                              </MenuItem>
                            )}
                          </MenuList>
                        </Portal>
                      </Menu>
                    ) : (
                      <Text fontSize={isChatOpen ? "xs" : "sm"} mb={isChatOpen ? 0 : 1}>{column !== null ? column : "-"}</Text>
                    )}
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default DataTable;